/**
 * SalesOverview — Vue d'ensemble Ventes (Sprint iter84, 13/02/2026).
 *
 * Le chef d'entreprise ouvre cette page → comprend son activité Ventes en
 * un coup d'œil sans avoir à naviguer :
 *   - 4 KPI stratégiques (mois, YTD, encours, DSO)
 *   - Alertes intelligentes cliquables (retards, DSO, quick-wins)
 *   - Objectif du mois + progress bar
 *   - Trend CA 12 mois (area chart)
 *   - Top 5 clients + Taux conversion devis
 *   - 5 prochaines échéances + 5 derniers paiements
 *   - Actions rapides globales
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip,
} from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  TrendingUp, TrendingDown, AlertTriangle, Clock, CheckCircle2,
  Building2, Calendar, Plus, FileText, FileSignature, Target,
  Sparkles, Loader2, ArrowRight, Star, Mail,
} from "lucide-react";
import { cn } from "@/lib/utils";
import SalesOnboardingGate from "@/components/sales/SalesOnboardingGate";

const eur = (n, p = 0) => `${(Number(n) || 0).toLocaleString("fr-FR", {
  minimumFractionDigits: p, maximumFractionDigits: p,
})} €`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", {
  day: "2-digit", month: "short",
}) : "—";
const fmtMonth = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const months = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin",
                   "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
  return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

const ALERT_META = {
  danger:  { cls: "bg-red-50 border-red-200 text-red-800", Icon: AlertTriangle },
  warning: { cls: "bg-amber-50 border-amber-200 text-amber-800", Icon: Clock },
  info:    { cls: "bg-blue-50 border-blue-200 text-blue-800", Icon: FileText },
  success: { cls: "bg-emerald-50 border-emerald-200 text-emerald-800", Icon: CheckCircle2 },
};

function BigKpi({ label, value, sub, delta, icon: Icon, accent = "blue", idx = 0, testid }) {
  const accents = {
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    purple: "bg-purple-50 text-purple-700",
  };
  const positive = typeof delta === "number" && delta >= 0;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.04 }}>
      <Card className="p-5 bg-white border-slate-200 rounded-xl card-hover" data-testid={testid}>
        <div className="flex items-start justify-between mb-3">
          <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center", accents[accent])}>
            <Icon className="h-4 w-4" strokeWidth={1.75} />
          </div>
          {typeof delta === "number" && (
            <div className={cn("flex items-center gap-1 text-xs font-medium",
              positive ? "text-emerald-700" : "text-red-700")}>
              {positive ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
              {positive ? "+" : ""}{delta.toFixed(1)} %
            </div>
          )}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">{label}</div>
        <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 font-mono tabular-nums"
              style={{ fontFamily: "Manrope, sans-serif" }}>
          {value}
        </div>
        {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
      </Card>
    </motion.div>
  );
}

export default function SalesOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState("month");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState("all");
  // ── Compare mode ──
  const [compareOn, setCompareOn] = useState(false);
  const [comparePreset, setComparePreset] = useState("last_year");
  const [compareYear, setCompareYear] = useState(new Date().getFullYear() - 1);
  const [compareMonth, setCompareMonth] = useState("all");

  const MONTHS_FR = [
    { value: "all", label: "Toute l'année" },
    { value: "01", label: "Janvier" }, { value: "02", label: "Février" },
    { value: "03", label: "Mars" }, { value: "04", label: "Avril" },
    { value: "05", label: "Mai" }, { value: "06", label: "Juin" },
    { value: "07", label: "Juillet" }, { value: "08", label: "Août" },
    { value: "09", label: "Septembre" }, { value: "10", label: "Octobre" },
    { value: "11", label: "Novembre" }, { value: "12", label: "Décembre" },
  ];
  const YEARS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (preset && preset !== "custom") params.preset = preset;
      else {
        params.year = year;
        if (month !== "all") params.month = month;
      }
      if (compareOn) {
        if (comparePreset && comparePreset !== "custom") params.compare_preset = comparePreset;
        else {
          params.compare_year = compareYear;
          if (compareMonth !== "all") params.compare_month = compareMonth;
        }
      }
      // Charge overview + vigilance en parallèle (mois courant)
      const nowKey = new Date().toISOString().slice(0, 7);
      const [ov, vig] = await Promise.all([
        api.get("/sales/overview", { params }),
        api.get("/vigilance", { params: { month: nowKey } }).catch(() => ({ data: { items: [] } })),
      ]);
      // Injecte les alertes vigilance en tête (récurrences manquantes)
      const vigItems = (vig.data?.items || []).filter((v) => !v.ignored);
      const vigAlerts = vigItems.length > 0 ? [{
        level: "warning",
        message: `${vigItems.length} récurrence(s) client(s) manquante(s) ce mois — factures attendues absentes`,
        action_label: "Voir Vigilance",
        action_route: "/app/vigilance",
      }] : [];
      const merged = { ...ov.data, alerts: [...vigAlerts, ...(ov.data.alerts || [])] };
      setData(merged);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [
    preset, year, month, compareOn, comparePreset, compareYear, compareMonth,
  ]);

  const applyPreset = (p) => {
    setPreset(p);
    if (p === "last_year") { setYear(new Date().getFullYear() - 1); setMonth("all"); }
    else if (p === "ytd")   { setYear(new Date().getFullYear());     setMonth("all"); }
    else if (p === "month") { setYear(new Date().getFullYear()); setMonth(new Date().toISOString().slice(5, 7)); }
  };

  const setYearManual = (y) => { setYear(y); setPreset("custom"); };
  const setMonthManual = (m) => { setMonth(m); setPreset("custom"); };

  if (loading || !data) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500" data-testid="sales-overview-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement du tableau de bord Ventes…
      </div>
    );
  }

  const cm = data.current_month;
  const yt = data.ytd;
  const outs = data.outstanding;
  const conv = data.quote_conversion;
  const trendData = (data.trend_12m || []).map((b) => ({ month: fmtMonth(b.month), CA: b.ca_ttc }));
  const targetPct = Math.min(cm.target_pct, 100);

  return (
    <div className="space-y-6" data-testid="sales-overview-page">
      <SalesOnboardingGate />
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Vue d'ensemble Ventes
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Pilotage complet — période <span className="font-semibold text-slate-700">{data.period?.label || cm.period}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/app/vigilance")}
                   data-testid="qa-vigilance-btn"
                   className="border-amber-200 text-amber-700 hover:bg-amber-50">
            <AlertTriangle className="w-4 h-4 mr-1.5" /> Vigilance
          </Button>
          <Button variant="outline" onClick={() => navigate("/app/quotes")}
                   data-testid="qa-quote-btn">
            <FileSignature className="w-4 h-4 mr-1.5" /> Nouveau devis
          </Button>
          <Button onClick={() => navigate("/app/invoices/new")}
                   className="bg-blue-600 hover:bg-blue-700 text-white"
                   data-testid="qa-invoice-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nouvelle facture
          </Button>
        </div>
      </div>

      {/* Period Filters — chips + dropdowns */}
      <Card className="p-3 bg-white border-slate-200 rounded-xl" data-testid="period-filters">
        <div className="flex flex-wrap items-center gap-2">
          {[
            { key: "month",     label: "Mois en cours" },
            { key: "quarter",   label: "Trimestre" },
            { key: "ytd",       label: "Cumul annuel" },
            { key: "12m",       label: "12 mois glissants" },
            { key: "last_year", label: `Année ${new Date().getFullYear() - 1}` },
          ].map((p) => (
            <button key={p.key}
                    onClick={() => applyPreset(p.key)}
                    data-testid={`preset-${p.key}`}
                    className={cn(
                      "text-xs px-3 py-1.5 rounded-full border transition-colors",
                      preset === p.key
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
              {p.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <select id="sales-month-select" name="sales-month" value={month} onChange={(e) => setMonthManual(e.target.value)}
                    aria-label="Filtrer par mois"
                    className="text-xs px-2 py-1.5 rounded-md border border-slate-200 bg-white"
                    data-testid="month-select">
              {MONTHS_FR.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <select id="sales-year-select" name="sales-year" value={year} onChange={(e) => setYearManual(Number(e.target.value))}
                    aria-label="Filtrer par année"
                    className="text-xs px-2 py-1.5 rounded-md border border-slate-200 bg-white"
                    data-testid="year-select">
              {YEARS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button onClick={() => setCompareOn((v) => !v)}
                    data-testid="toggle-compare"
                    className={cn(
                      "text-xs px-3 py-1.5 rounded-full border transition-colors ml-2",
                      compareOn
                        ? "bg-purple-100 border-purple-300 text-purple-800"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
              <TrendingUp className="w-3 h-3 inline mr-1" />
              {compareOn ? "Comparaison active" : "Comparer avec…"}
            </button>
          </div>
        </div>
        {compareOn && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2"
                data-testid="compare-row">
            <span className="text-xs text-purple-700 font-medium">
              🆚 Comparer avec :
            </span>
            {[
              { key: "last_year", label: `Année ${new Date().getFullYear() - 1}` },
              { key: "month",     label: "Mois en cours" },
              { key: "quarter",   label: "Trimestre" },
              { key: "ytd",       label: "Cumul annuel" },
            ].map((p) => (
              <button key={p.key}
                      onClick={() => { setComparePreset(p.key); setCompareMonth("all"); }}
                      data-testid={`compare-preset-${p.key}`}
                      className={cn(
                        "text-xs px-2.5 py-1 rounded-full border transition-colors",
                        comparePreset === p.key
                          ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
                {p.label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <select id="sales-compare-month-select" name="sales-compare-month" value={compareMonth}
                      onChange={(e) => { setCompareMonth(e.target.value); setComparePreset("custom"); }}
                      aria-label="Comparer avec le mois"
                      className="text-xs px-2 py-1 rounded-md border border-slate-200 bg-white"
                      data-testid="compare-month-select">
                {MONTHS_FR.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <select id="sales-compare-year-select" name="sales-compare-year" value={compareYear}
                      onChange={(e) => { setCompareYear(Number(e.target.value)); setComparePreset("custom"); }}
                      aria-label="Comparer avec l'année"
                      className="text-xs px-2 py-1 rounded-md border border-slate-200 bg-white"
                      data-testid="compare-year-select">
                {YEARS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        {compareOn && data.comparison && (
          <div className="mt-3 p-3 rounded-lg bg-purple-50 border border-purple-200"
                data-testid="compare-result">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase text-purple-700 font-medium">Période B</div>
                <div className="font-semibold text-purple-900 mt-0.5">{data.comparison.label}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500 font-medium">CA HT · B</div>
                <div className="font-mono font-semibold text-slate-800 mt-0.5">
                  {eur(data.comparison.ca_ht)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500 font-medium">Δ CA (A vs B)</div>
                {data.comparison.delta_ht_pct == null ? (
                  <div className="text-slate-400 mt-0.5">—</div>
                ) : (
                  <div className={cn("font-mono font-semibold mt-0.5 flex items-center gap-1",
                    data.comparison.delta_ht_pct >= 0 ? "text-emerald-700" : "text-red-700")}>
                    {data.comparison.delta_ht_pct >= 0
                      ? <TrendingUp className="w-3.5 h-3.5" />
                      : <TrendingDown className="w-3.5 h-3.5" />}
                    {data.comparison.delta_ht_pct >= 0 ? "+" : ""}
                    {data.comparison.delta_ht_pct.toFixed(1)} %
                  </div>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500 font-medium">Δ Factures</div>
                {data.comparison.delta_count_pct == null ? (
                  <div className="text-slate-400 mt-0.5">—</div>
                ) : (
                  <div className={cn("font-mono font-semibold mt-0.5",
                    data.comparison.delta_count_pct >= 0 ? "text-emerald-700" : "text-red-700")}>
                    {data.comparison.count} factures ({data.comparison.delta_count_pct >= 0 ? "+" : ""}
                    {data.comparison.delta_count_pct.toFixed(0)} %)
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Alertes intelligentes */}
      {(data.alerts || []).length > 0 && (
        <div data-testid="zone-actions">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-6 w-1 rounded-full bg-red-500" />
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-700">
              À traiter maintenant
            </h2>
            <span className="text-xs text-slate-400">
              · {data.alerts.length} action{data.alerts.length > 1 ? "s" : ""}
            </span>
          </div>
          <div className="space-y-2" data-testid="alerts-block">
            {data.alerts.map((a, i) => {
            const { cls, Icon } = ALERT_META[a.level] || ALERT_META.warning;
            const ClickWrap = a.action_route
              ? ({ children }) => (
                <button onClick={() => navigate(a.action_route)}
                        className="w-full text-left"
                        data-testid={`alert-${a.level}-${i}`}>
                  {children}
                </button>
              )
              : ({ children }) => <div data-testid={`alert-${a.level}-${i}`}>{children}</div>;
            return (
              <ClickWrap key={i}>
                <div className={cn(
                  "px-4 py-3 rounded-lg border flex items-center gap-3 text-sm transition-colors",
                  cls,
                  a.action_route && "hover:brightness-95")}>
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1">{a.message}</span>
                  {a.action_label && (
                    <span className="text-xs font-medium underline flex items-center gap-1">
                      {a.action_label} <ArrowRight className="w-3 h-3" />
                    </span>
                  )}
                </div>
              </ClickWrap>
            );
          })}
          </div>
        </div>
      )}

      {/* Zone 2 — Pilotage du mois */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="h-6 w-1 rounded-full bg-blue-500" />
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-700">
            Pilotage — {data.period?.label || cm.period}
          </h2>
        </div>
      </div>

      {/* 4 Big KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <BigKpi label={`CA · ${data.period?.label || cm.period}`}
                 value={eur(cm.ca_ht)}
                 sub={`${cm.count} facture${cm.count > 1 ? "s" : ""} · TTC ${eur(cm.ca_ttc)}`}
                 delta={cm.delta_pct_vs_last_year}
                 icon={TrendingUp} accent="blue" idx={0}
                 testid="kpi-month" />
        <BigKpi label="Panier moyen période"
                 value={eur(cm.count > 0 ? cm.ca_ht / cm.count : 0)}
                 sub={cm.count > 0 ? `sur ${cm.count} facture${cm.count > 1 ? "s" : ""}` : "aucune facture"}
                 icon={Building2} accent="emerald" idx={1}
                 testid="kpi-ytd" />
        <BigKpi label="Encours clients"
                 value={eur(outs.total_ttc)}
                 sub={`${outs.count} facture${outs.count > 1 ? "s" : ""} en attente`}
                 icon={Calendar}
                 accent={outs.overdue_60_plus.count > 0 ? "red" : outs.overdue_30_60.count > 0 ? "amber" : "purple"}
                 idx={2} testid="kpi-outstanding" />
        <BigKpi label="DSO tenant"
                 value={data.dso_days == null ? "—" : `${data.dso_days > 0 ? "+" : ""}${data.dso_days} j`}
                 sub={data.dso_days == null ? "aucune facture payée récente"
                        : data.dso_days > 15 ? "élevé — clients payent tard"
                        : data.dso_days > 0 ? "correct" : "excellent — payé avant échéance"}
                 icon={Clock}
                 accent={data.dso_days == null ? "purple" : data.dso_days > 15 ? "red" : data.dso_days > 0 ? "amber" : "emerald"}
                 idx={3} testid="kpi-dso" />
      </div>

      {/* Row: Objectif + Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-4 p-5 bg-white border-slate-200 rounded-xl" data-testid="target-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">
                Objectif du mois
              </div>
              <div className="text-lg font-semibold text-slate-900 font-mono mt-1"
                    style={{ fontFamily: "Manrope, sans-serif" }}>
                {eur(cm.ca_ht)} <span className="text-slate-400 text-sm">/ {eur(cm.target)}</span>
              </div>
            </div>
            <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center",
              cm.target_pct >= 100 ? "bg-emerald-50 text-emerald-700"
              : cm.target_pct >= 50 ? "bg-blue-50 text-blue-700"
              : "bg-amber-50 text-amber-700")}>
              <Target className="w-4 h-4" />
            </div>
          </div>
          <Progress value={targetPct} className="h-2" />
          <div className="flex justify-between mt-2 text-xs">
            <span className={cn("font-medium",
              cm.target_pct >= 100 ? "text-emerald-700"
              : cm.target_pct >= 50 ? "text-blue-700"
              : "text-amber-700")}>
              {cm.target_pct.toFixed(0)} %
            </span>
            <Link to="/app/analyse-ca" className="text-slate-500 hover:text-slate-800 underline">
              Modifier l'objectif
            </Link>
          </div>
        </Card>

        <Card className="lg:col-span-8 p-5 bg-white border-slate-200 rounded-xl" data-testid="trend-chart">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900"
                   style={{ fontFamily: "Manrope, sans-serif" }}>
                CA 12 mois glissants
              </h3>
              <p className="text-xs text-slate-500">Somme TTC des factures émises + payées</p>
            </div>
            <Link to="/app/analyse-ca" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              Analyse détaillée <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="sales-ca-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false}
                      tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
              <RTooltip contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }}
                         formatter={(v) => [eur(v), "CA TTC"]} />
              <Area type="monotone" dataKey="CA" stroke="#2563eb" strokeWidth={2} fill="url(#sales-ca-fill)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Row: Top clients + Devis conversion */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="h-6 w-1 rounded-full bg-slate-400" />
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-700">
            Tendance & conversions
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-7 p-5 bg-white border-slate-200 rounded-xl" data-testid="top-clients-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900"
                 style={{ fontFamily: "Manrope, sans-serif" }}>
              Top 5 clients (CA 12 mois)
            </h3>
            <Link to="/app/clients" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              Tous les clients <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {(data.top_clients || []).length === 0 ? (
            <div className="text-xs text-slate-400 italic py-6 text-center">Aucun client actif</div>
          ) : (
            <div className="space-y-2.5">
              {data.top_clients.map((c, i) => {
                const max = data.top_clients[0].ttc;
                const pct = max > 0 ? (c.ttc / max) * 100 : 0;
                return (
                  <button key={c.client_id}
                          onClick={() => navigate(`/app/clients/${c.client_id}`)}
                          className="w-full text-left group"
                          data-testid={`top-client-${i}`}>
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-semibold text-slate-600 shrink-0">
                          {i + 1}
                        </div>
                        <span className="text-sm font-medium text-slate-800 truncate group-hover:text-blue-700" title={c.name}>
                          {c.name}
                        </span>
                      </div>
                      <span className="text-sm font-mono font-semibold text-slate-900 shrink-0">
                        {eur(c.ttc)}
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all group-hover:brightness-110"
                            style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {c.count} facture{c.count > 1 ? "s" : ""}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="lg:col-span-5 p-5 bg-white border-slate-200 rounded-xl" data-testid="conversion-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900"
                 style={{ fontFamily: "Manrope, sans-serif" }}>
              Conversion devis
            </h3>
            <Link to="/app/quotes" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              Voir devis <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-3xl font-semibold text-slate-900 font-mono"
                   style={{ fontFamily: "Manrope, sans-serif" }}>
              {conv.rate_pct.toFixed(0)}%
            </span>
            <span className="text-xs text-slate-500">
              taux d'acceptation
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100">
              <div className="text-[10px] uppercase text-emerald-700 font-medium">Acceptés</div>
              <div className="text-base font-semibold font-mono text-emerald-900 mt-0.5">{conv.accepted}</div>
              <div className="text-[10px] text-emerald-700 font-mono mt-0.5">{eur(conv.accepted_ttc)}</div>
            </div>
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
              <div className="text-[10px] uppercase text-amber-700 font-medium">En attente</div>
              <div className="text-base font-semibold font-mono text-amber-900 mt-0.5">{conv.pending}</div>
              <div className="text-[10px] text-amber-700 mt-0.5">à relancer</div>
            </div>
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
              <div className="text-[10px] uppercase text-slate-500 font-medium">Envoyés</div>
              <div className="text-base font-semibold font-mono text-slate-800 mt-0.5">{conv.sent}</div>
            </div>
            <div className="p-3 rounded-lg bg-red-50 border border-red-100">
              <div className="text-[10px] uppercase text-red-700 font-medium">Expirés</div>
              <div className="text-base font-semibold font-mono text-red-900 mt-0.5">{conv.expired}</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Row: Prochaines échéances + Derniers paiements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 bg-white border-slate-200 rounded-xl" data-testid="upcoming-due-card">
          <h3 className="text-sm font-semibold text-slate-900 mb-3"
               style={{ fontFamily: "Manrope, sans-serif" }}>
            📅 Prochaines échéances
          </h3>
          {(data.upcoming_due || []).length === 0 ? (
            <div className="text-xs text-slate-400 italic py-6 text-center">
              Aucune facture à venir
            </div>
          ) : (
            <div className="space-y-1.5">
              {data.upcoming_due.map((inv) => (
                <button key={inv.id}
                        onClick={() => navigate(`/app/invoices/${inv.id}`)}
                        className="w-full text-left flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-slate-50"
                        data-testid={`upcoming-${inv.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-mono text-slate-800 truncate">{inv.number}</div>
                      <div className="text-[11px] text-slate-500 truncate">{inv.client_name}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-slate-500 font-mono">{fmtDate(inv.due_date)}</div>
                    <div className="text-sm font-mono font-semibold text-slate-800">{eur(inv.remaining_ttc)}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 bg-white border-slate-200 rounded-xl" data-testid="recent-paid-card">
          <h3 className="text-sm font-semibold text-slate-900 mb-3"
               style={{ fontFamily: "Manrope, sans-serif" }}>
            💰 Derniers paiements reçus (30 jours)
          </h3>
          {(data.recent_paid || []).length === 0 ? (
            <div className="text-xs text-slate-400 italic py-6 text-center">
              Aucun paiement récent
            </div>
          ) : (
            <div className="space-y-1.5">
              {data.recent_paid.map((inv) => (
                <button key={inv.id}
                        onClick={() => navigate(`/app/invoices/${inv.id}`)}
                        className="w-full text-left flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-slate-50"
                        data-testid={`recent-paid-${inv.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-mono text-slate-800 truncate">{inv.number}</div>
                      <div className="text-[11px] text-slate-500 truncate">{inv.client_name}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-slate-500 font-mono">{fmtDate(inv.paid_at)}</div>
                    <div className="text-sm font-mono font-semibold text-emerald-700">+{eur(inv.amount)}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
